import { c as _c } from "react/compiler-runtime";
import { WithClassName, WithDisabled, WithElementId } from '@/types/component';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { Button } from '@/comps/primitives/button';
import { Chip } from '@/comps/chip';
import React, { PropsWithChildren } from 'react';
import { IconType } from '@/constants/icons';
import { PopperContentProps } from '@radix-ui/react-popper';
import { XCircleIcon } from 'lucide-react';
import { cn } from '@/utils/shad';
type FilterPopoverUIProps = PropsWithChildren & WithClassName & WithDisabled & WithElementId & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  appliedFiltersText?: string;
  showAppliedFilters?: boolean;
  triggerContent?: React.ReactNode;
  align?: 'right' | 'left';
  icon?: IconType;
  triggerClassName?: string;
  side?: PopperContentProps['side'];
  nAppliedFilters: number;
  onFiltersClear: () => void;
  /** When false, the applied-filters chip shows counts/text but no clear (X) control */
  appliedFiltersClearable?: boolean;
  disabledMessage?: string;
  hasDraftEdits?: boolean;
  modal?: boolean;
};
export function FilterPopoverUI(t0) {
  const $ = _c(29);
  const {
    open,
    onOpenChange,
    disabled,
    disabledMessage,
    triggerContent,
    title,
    children,
    appliedFiltersText,
    showAppliedFilters,
    nAppliedFilters,
    align,
    icon,
    triggerClassName,
    side: t1,
    onFiltersClear,
    appliedFiltersClearable: t2,
    id,
    className,
    hasDraftEdits: t3,
    modal: t4
  } = t0;
  const side = t1 === undefined ? "bottom" : t1;
  const appliedFiltersClearable = t2 === undefined ? true : t2;
  const hasDraftEdits = t3 === undefined ? false : t3;
  const modal = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== appliedFiltersClearable || $[1] !== appliedFiltersText || $[2] !== disabled || $[3] !== disabledMessage || $[4] !== hasDraftEdits || $[5] !== icon || $[6] !== nAppliedFilters || $[7] !== onFiltersClear || $[8] !== showAppliedFilters || $[9] !== title || $[10] !== triggerContent) {
    t5 = triggerContent || <Button className="relative gap-1" variant="outline" PrependIcon={icon} disabled={disabled} disabledMessage={disabledMessage}>{hasDraftEdits && <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-n-violet-platform ring-2 ring-background" aria-hidden={true} />}<span>{title}</span>{showAppliedFilters && !!appliedFiltersText && <AppliedFiltersIndicator text={appliedFiltersText} nAppliedFilters={nAppliedFilters} onClear={onFiltersClear} clearable={appliedFiltersClearable} />}</Button>;
    $[0] = appliedFiltersClearable;
    $[1] = appliedFiltersText;
    $[2] = disabled;
    $[3] = disabledMessage;
    $[4] = hasDraftEdits;
    $[5] = icon;
    $[6] = nAppliedFilters;
    $[7] = onFiltersClear;
    $[8] = showAppliedFilters;
    $[9] = title;
    $[10] = triggerContent;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== id || $[13] !== t5 || $[14] !== triggerClassName) {
    t6 = <PopoverTrigger id={id} asChild={true} className={triggerClassName}>{t5}</PopoverTrigger>;
    $[12] = id;
    $[13] = t5;
    $[14] = triggerClassName;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== className) {
    t7 = cn("h-125 max-w-150 min-w-125 overflow-hidden bg-background p-0", className);
    $[16] = className;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = align === "right" ? "start" : "end";
  let t9;
  if ($[18] !== children || $[19] !== side || $[20] !== t7 || $[21] !== t8) {
    t9 = <PopoverContent className={t7} align={t8} side={side} onWheel={_temp}>{children}</PopoverContent>;
    $[18] = children;
    $[19] = side;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== modal || $[24] !== onOpenChange || $[25] !== open || $[26] !== t6 || $[27] !== t9) {
    t10 = <Popover open={open} onOpenChange={onOpenChange} modal={modal}>{t6}{t9}</Popover>;
    $[23] = modal;
    $[24] = onOpenChange;
    $[25] = open;
    $[26] = t6;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function _temp(e) {
  return e.stopPropagation();
}
type AppliedFilterIndicatorProps = WithClassName & {
  text: string;
  nAppliedFilters: number;
  onClear: () => void;
  clearable?: boolean;
};
export function AppliedFiltersIndicator(t0) {
  const $ = _c(17);
  const {
    text,
    nAppliedFilters,
    onClear,
    clearable: t1,
    className
  } = t0;
  const clearable = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["#6366F1", "#84CC16", "#06B6D4", "#60A5FA"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const dotColors = t2;
  const dotsToShow = Math.min(nAppliedFilters, 4);
  let t3;
  if ($[1] !== onClear) {
    t3 = e => {
      e.stopPropagation();
      onClear();
    };
    $[1] = onClear;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleClearClick = t3;
  let t4;
  if ($[3] !== className) {
    t4 = cn("relative ml-2 flex max-w-[min(280px,42vw)] min-w-0 items-center gap-1.5 rounded-full bg-[#606ACB80] py-0 pl-2 text-[10px] text-white", className);
    $[3] = className;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== dotsToShow) {
    t5 = <div className="flex shrink-0 -space-x-1">{Array.from({
        length: dotsToShow
      }).map((_, index) => <div key={index} className="h-2 w-2 rounded-full" style={{
        backgroundColor: dotColors[index]
      }} />)}</div>;
    $[5] = dotsToShow;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== text) {
    t6 = <span className="min-w-0 truncate">{text}</span>;
    $[7] = text;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== clearable || $[10] !== handleClearClick) {
    t7 = clearable && <div tabIndex={0} role="button" className="shrink-0 text-muted-foreground transition-colors hover:cursor-pointer hover:text-foreground" onClick={ev => {
      ev.stopPropagation();
      handleClearClick(ev);
    }} onKeyDown={e_0 => {
      if (e_0.key === "Enter") {
        handleClearClick(e_0 as unknown as React.MouseEvent);
      }
    }}><XCircleIcon className="h-3.5 w-3.5" size={10} /></div>;
    $[9] = clearable;
    $[10] = handleClearClick;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <Chip size="small" className={t4} variant="mutedv2">{t5}{t6}{t7}</Chip>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}